import * as React from "react";
import { CalendarCheck, ExternalLink, RotateCcw, Send } from "lucide-react";
import { Link } from "react-router-dom";

import {
  ClarificationKind,
  ClarificationOption,
  ConfirmationMode,
  MeetingInterpretation,
  MentionCandidate,
} from "@/api/types";
import { NeuralHead } from "@/components/comic";
import { CohortIntentCards } from "./CohortIntentCards";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

import { calendarLinkFor } from "@/features/meetings/shared";

import { CALENDAR_SUFFIX } from "./a2aSlots";
import { ChatEntry, TransmissionMode } from "./types";
import { DateRangePicker } from "./DateRangePicker";
import { MeetingContextCard } from "./MeetingContextCard";
import { extractHandles, activeMention, type ActiveMention } from "../mentions";
import {
  activeSigil,
  renderSigil,
  SIGIL_OPEN,
  SIGIL_TYPES,
  type ActiveSigil,
  type SigilKind,
} from "../sigils";

/**
 * Effective conversation context per entry, for delineating the chat log.
 *
 * <p>Entries without a {@code conversationId} (live user messages, replies
 * to clarifications, error notes) belong to the conversation that FOLLOWS
 * them — a user message opens the negotiation its agent reply confirms — so
 * nulls backfill from the next identified entry; trailing nulls inherit
 * the previous context. A divider belongs wherever the resulting id changes
 * between consecutive entries.</p>
 */
export function contextIds(entries: ChatEntry[]): (string | null)[] {
  // A proposal carries no conversation id BY DESIGN — a cohort batch is not a negotiation — so it
  // is a null here and backfills from its neighbours like any other unidentified entry.
  const ids = entries.map((m) => ("conversationId" in m ? (m.conversationId ?? null) : null));
  for (let i = ids.length - 2; i >= 0; i--) {
    if (ids[i] == null) ids[i] = ids[i + 1];
  }
  for (let i = 1; i < ids.length; i++) {
    if (ids[i] == null) ids[i] = ids[i - 1];
  }
  return ids;
}

/**
 * Derived opening labels: where the context id changes on a NORMAL entry
 * (explicit divider entries are their own boundary), label the boundary
 * "new request" — or "continued" when the conversation was already on
 * screen and is resuming after an interleaved block.
 */
function derivedLabels(
  entries: ChatEntry[],
  contexts: (string | null)[],
): (string | null)[] {
  const seen = new Set<string | null>();
  return entries.map((entry, i) => {
    const boundary =
      i > 0 && contexts[i] !== contexts[i - 1] && entry.from !== "divider";
    const label = boundary
      ? seen.has(contexts[i])
        ? "continued"
        : "new request"
      : null;
    seen.add(contexts[i]);
    return label;
  });
}

/** Dotted separator bounding one conversation's block in the chat log. */
function ContextDivider({
  label,
  conversationId,
}: {
  label: string;
  conversationId: string | null;
}) {
  return (
    <div className="flex items-center gap-2 py-1.5" role="separator">
      <div className="h-px flex-1 bg-border" />
      <span className="font-display text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {`${label}${conversationId ? ` · #${conversationId.slice(0, 8)}` : ""}`}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

/**
 * Comic-styled chat panel. Renders the conversation log on top, a composer
 * on the bottom. Pure presentation — the caller owns the message list and
 * the draft.
 *
 * <p>SYSTEM bubbles are filtered out: they live in the per-conversation
 * drawer instead. Agent bubbles that carry a {@code conversationId} render
 * a "View negotiation" link that calls {@code onOpenConversation} so the
 * caller can open the drawer.</p>
 */
/**
 * One thing a sigil could name: today, an availability pattern the caller owns.
 *
 * `name` is what goes between the braces, so it is the label exactly as stored, spaces and
 * all. `detail` is the one line of context the row shows on the right.
 */
export interface SigilCandidate {
  name: string;
  detail: string;
}

/** One row of either sigil menu: the three types, or the things a settled type can name. */
interface SigilRow {
  key: string;
  label: string;
  hint: string;
  disabled: boolean;
  onPick: () => void;
}

/** How many characters the type marker takes inside the braces. One for every type. */
function markerLength(kind: SigilKind): number {
  return SIGIL_TYPES.find((t) => t.kind === kind)!.marker.length;
}

/** What the pattern pill inserts and shows: the prefix WITH its type character, `.{#`. */
const PATTERN_PREFIX = SIGIL_OPEN + SIGIL_TYPES.find((t) => t.kind === "pattern")!.marker;

export function ConversationPanel({
  mode = "outgoing",
  messages,
  draft,
  onDraftChange,
  onSubmit,
  busy,
  emptyHint,
  footer,
  onNewThread,
  onOpenConversation,
  onConfirmIntent,
  onDropIntent,
  onAnswerIntent,
  busyIntentId,
  optionalHandles,
  onToggleOptional,
  quickReplies,
  onQuickReply,
  onMentionSearch,
  onSigilSearch,
  confirmMode = "auto",
  onConfirmModeChange,
  clarifyKind = "choice",
  interpretation,
  locked = false,
  belowComposer,
}: {
  mode?: TransmissionMode;
  messages: ChatEntry[];
  draft: string;
  onDraftChange: (next: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  busy: boolean;
  emptyHint?: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * Abandon the current (possibly stuck) conversation and start fresh.
   * When provided, a subdued "New thread" control shows in the panel header.
   */
  onNewThread?: () => void;
  onOpenConversation?: (conversationId: string) => void;
  /**
   * Say yes to ONE intent of a cohort proposal. Per intent, so a reschedule can be agreed while its
   * sibling cancel is still being argued with.
   */
  onConfirmIntent?: (batchId: string, intentId: string) => void;
  /** Throw ONE intent away, when refining it has gone wrong. */
  onDropIntent?: (batchId: string, intentId: string) => void;
  /** A quick-reply answer to ONE card's question, scoped to it (intent_id). */
  onAnswerIntent?: (batchId: string, intentId: string, answer: string) => void;
  /** The intent whose button was just pressed, so only that card goes quiet. */
  busyIntentId?: string | null;
  /** Handles the user marked optional; the rest are required. */
  optionalHandles?: Set<string>;
  onToggleOptional?: (handle: string) => void;
  /** Quick-reply choices for a pending clarification (availability patterns, time ranges…). */
  quickReplies?: ClarificationOption[];
  onQuickReply?: (option: ClarificationOption) => void;
  /**
   * The confirm-before-booking toggle. `pre_confirm` makes the agent echo its
   * interpretation for a go-ahead before contacting anyone; `auto` proceeds
   * unless the interpretation is a guess. When {@code onConfirmModeChange} is
   * provided a small toggle shows above the composer.
   */
  confirmMode?: ConfirmationMode;
  onConfirmModeChange?: (mode: ConfirmationMode) => void;
  /** Which input widget the current clarification wants (default: choice). */
  clarifyKind?: ClarificationKind;
  /** The parsed "meeting so far" to show as a card above the options, if any. */
  interpretation?: MeetingInterpretation | null;
  /**
   * Render the panel but refuse to send anything. The signed-out view shows the real chat so a
   * visitor can see what the product is, and cannot use it until they have proved who they are
   * (D-37). Locked is not the same as busy: busy means a turn is in flight and will finish.
   */
  locked?: boolean;
  /** Slot beneath the composer. The signed-out view puts its sign-in choices here. */
  belowComposer?: React.ReactNode;
  /**
   * Fetch @-mention suggestions for the partial handle `q` (empty = the
   * caller's most-frequent contacts). When provided, an autocomplete dropdown
   * shows while the caret is inside an `@token` (outgoing mode only).
   */
  onMentionSearch?: (q: string) => Promise<MentionCandidate[]>;
  /**
   * Fetch the things a settled sigil type can name, filtered by `q`. When provided, typing
   * `.{` opens the type chooser and picking a type opens this list (outgoing mode only).
   */
  onSigilSearch?: (kind: SigilKind, q: string) => Promise<SigilCandidate[]>;
}) {
  const endRef = React.useRef<HTMLDivElement>(null);
  const visible = React.useMemo(
    () => messages.filter((m) => m.from !== "system"),
    [messages],
  );
  const { contexts, openers } = React.useMemo(() => {
    const contexts = contextIds(visible);
    return { contexts, openers: derivedLabels(visible, contexts) };
  }, [visible]);
  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [visible.length]);

  // ----- @-mention autocomplete -----
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [mention, setMention] = React.useState<ActiveMention | null>(null);
  const [candidates, setCandidates] = React.useState<MentionCandidate[]>([]);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const mentionOpen =
    mode === "outgoing" &&
    !!onMentionSearch &&
    mention !== null &&
    candidates.length > 0;

  // Recompute the active mention from the textarea's live caret position.
  const syncMention = React.useCallback((el: HTMLTextAreaElement) => {
    const caret = el.selectionStart ?? el.value.length;
    setMention(activeMention(el.value, caret));
  }, []);

  // Debounced fetch whenever the active mention's query changes.
  React.useEffect(() => {
    if (mode !== "outgoing" || !onMentionSearch || mention === null) {
      setCandidates([]);
      return;
    }
    const q = mention.query;
    let cancelled = false;
    const t = window.setTimeout(() => {
      onMentionSearch(q)
        .then((res) => {
          if (!cancelled) {
            setCandidates(res);
            setActiveIndex(0);
          }
        })
        .catch(() => {
          if (!cancelled) setCandidates([]);
        });
    }, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [mode, onMentionSearch, mention]);

  // ----- .{ } sigil menu (pick the type first; the character is mandatory) -----
  const [sigil, setSigil] = React.useState<ActiveSigil | null>(null);
  const [sigilItems, setSigilItems] = React.useState<SigilCandidate[]>([]);
  const [sigilIndex, setSigilIndex] = React.useState(0);
  const sigilEnabled = mode === "outgoing" && !!onSigilSearch;
  // Two states, and which one is showing is decided by whether a type is settled. `.{` alone
  // shows the three types, which is what teaches the grammar; `.{#` has already picked one, so
  // it skips the chooser rather than charging an expert a step for knowing the character. An
  // unmarked body (`.{Fam`) keeps the chooser open, prompting for the type the grammar now
  // requires, rather than quietly resolving to nothing.
  const sigilChooserOpen = sigilEnabled && sigil !== null && sigil.kind === null;
  const sigilListOpen = sigilEnabled && sigil !== null && sigil.kind !== null;
  const sigilOpen = sigilChooserOpen || sigilListOpen;
  const sigilRows: SigilRow[] = sigilChooserOpen
    ? SIGIL_TYPES.map((type) => ({
        key: type.kind,
        label: type.label,
        hint: `${SIGIL_OPEN}${type.marker}`,
        disabled: !type.resolvable,
        onPick: () => chooseSigilType(type.kind, type.marker),
      }))
    : sigilItems.map((item) => ({
        key: item.name,
        label: item.name,
        hint: item.detail,
        disabled: false,
        onPick: () => chooseSigilItem(item),
      }));

  const syncSigil = React.useCallback((el: HTMLTextAreaElement) => {
    const caret = el.selectionStart ?? el.value.length;
    setSigil(activeSigil(el.value, caret));
  }, []);

  // Debounced fetch once a type is settled. The chooser needs no fetch: its three rows are the
  // grammar itself and never vary. An org marker settles a type nothing can resolve yet, so its
  // list simply comes back empty.
  React.useEffect(() => {
    if (!sigilEnabled || sigil === null || sigil.kind === null) {
      setSigilItems([]);
      return;
    }
    const { kind, query } = sigil;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      onSigilSearch!(kind, query)
        .then((found) => {
          if (!cancelled) {
            setSigilItems(found);
            setSigilIndex(0);
          }
        })
        .catch(() => {
          if (!cancelled) setSigilItems([]);
        });
    }, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [sigilEnabled, onSigilSearch, sigil]);

  React.useEffect(() => {
    if (sigilChooserOpen) setSigilIndex(0);
  }, [sigilChooserOpen]);

  /**
   * Insert the type's marker, which settles the kind and moves the menu on to the list. What
   * was typed after `.{` is kept: it was a name being typed before its type, not a mistake.
   */
  function chooseSigilType(kind: SigilKind, marker: string) {
    if (!sigil) return;
    const type = SIGIL_TYPES.find((t) => t.kind === kind)!;
    if (!type.resolvable) return;
    const before = draft.slice(0, sigil.start);
    const after = draft.slice(sigil.start + SIGIL_OPEN.length + sigil.query.length);
    const insert = `${SIGIL_OPEN}${marker}${sigil.query}`;
    onDraftChange(before + insert + after);
    restoreCaret(before.length + insert.length);
  }

  /** Replace the half-typed sigil with the finished one. */
  function chooseSigilItem(item: SigilCandidate) {
    if (!sigil || sigil.kind === null) return;
    const before = draft.slice(0, sigil.start);
    const after = draft.slice(sigil.start + SIGIL_OPEN.length + markerLength(sigil.kind) + sigil.query.length);
    const insert = `${renderSigil(sigil.kind, item.name)} `;
    onDraftChange(before + insert + after);
    setSigil(null);
    setSigilItems([]);
    restoreCaret(before.length + insert.length);
  }

  /** Put the caret back where the replacement left off, after React has re-rendered. */
  function restoreCaret(caret: number) {
    window.requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
        setSigil(activeSigil(el.value, caret));
      }
    });
  }

  /**
   * Insert the pattern prefix at the caret (replacing any selection), so the notation is
   * reachable from a button as well as by typing it. The button inserts `.{#`, not `.{`: it is
   * the pattern pill, and the marker is the type (#101), so pressing it opens the caller's
   * patterns directly rather than the chooser. The caret lands right after the prefix, and
   * {@link restoreCaret} re-runs the sigil detection there, which is what opens the menu:
   * inserting the text alone would not.
   */
  function insertSigilPrefix() {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    onDraftChange(draft.slice(0, start) + PATTERN_PREFIX + draft.slice(end));
    restoreCaret(start + PATTERN_PREFIX.length);
  }

  // Replace the active @token with the chosen handle, then restore the caret.
  function chooseCandidate(c: MentionCandidate) {
    if (!mention) return;
    const before = draft.slice(0, mention.start);
    const after = draft.slice(mention.start + 1 + mention.query.length);
    const insert = `@${c.handle} `;
    onDraftChange(before + insert + after);
    setMention(null);
    setCandidates([]);
    const caret = before.length + insert.length;
    window.requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    });
  }

  function onComposerKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // The sigil menu takes the keys first when it is open. The two can never both be open —
    // an `@` inside braces is mid-word, so it is not a mention — but ordering them makes that
    // an invariant rather than a coincidence.
    if (sigilOpen && sigilRows.length > 0) {
      onMenuKeyDown(e, sigilRows.length, sigilIndex, setSigilIndex, () => {
        const row = sigilRows[sigilIndex];
        if (!row.disabled) row.onPick();
      }, () => setSigil(null));
      return;
    }
    if (!mentionOpen) return;
    onMenuKeyDown(e, candidates.length, activeIndex, setActiveIndex,
      () => chooseCandidate(candidates[activeIndex]), () => setMention(null));
  }

  /** Arrow to move, Enter or Tab to take, Escape to dismiss. Shared by both menus. */
  function onMenuKeyDown(
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    count: number,
    index: number,
    setIndex: (next: number) => void,
    take: () => void,
    dismiss: () => void,
  ) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((index + 1) % count);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((index - 1 + count) % count);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      take();
    } else if (e.key === "Escape") {
      e.preventDefault();
      dismiss();
    }
  }

  return (
    <div className="comic-shadow flex flex-col overflow-hidden rounded-3xl border border-border/70 bg-card">
      {/* Header — the agent's identity + status, so the chat reads as a
          conversation with a named counterpart rather than a form. */}
      <header className="flex items-center justify-between gap-2 border-b border-border/60 px-5 py-3">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-blue/10 p-1.5">
            <NeuralHead />
          </span>
          <div className="leading-tight">
            <div className="font-display text-sm font-semibold text-foreground">
              {mode === "outgoing" ? "Your agent" : "Incoming request"}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  busy ? "animate-pulse bg-amber-500" : "bg-brand-teal",
                )}
              />
              {busy ? "Working on it…" : "Ready when you are"}
            </div>
          </div>
        </div>
        {mode === "outgoing" && onNewThread && (
          <button
            type="button"
            onClick={onNewThread}
            disabled={busy}
            title="Abandon this conversation and start a new thread"
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:bg-brand-blue/10 hover:text-brand-blue disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RotateCcw className="h-3 w-3" />
            New thread
          </button>
        )}
      </header>

      {/* Canvas — a soft distinct surface the bubbles float on. */}
      <div className="bg-gradient-to-b from-muted/40 to-muted/10">
        <ScrollArea className="h-[452px] px-4 py-3">
          <div className="flex flex-col">
            {visible.length === 0 && emptyHint && (
              <p className="px-2 py-6 text-center text-sm italic text-muted-foreground">
                {emptyHint}
              </p>
            )}
            {visible.map((entry, i) => {
              if (entry.from === "divider") {
                return (
                  <ContextDivider
                    key={i}
                    label={entry.label}
                    conversationId={entry.conversationId}
                  />
                );
              }
              if (entry.from === "proposal") {
                return (
                  <React.Fragment key={i}>
                    {openers[i] && (
                      <ContextDivider label={openers[i]!} conversationId={contexts[i]} />
                    )}
                    <div className="mt-2.5">
                      <CohortIntentCards
                        batch={entry.batch}
                        busyIntentId={busyIntentId}
                        onConfirm={(intentId) => onConfirmIntent?.(entry.batch.id!, intentId)}
                        onDrop={(intentId) => onDropIntent?.(entry.batch.id!, intentId)}
                        onAnswer={(intentId, answer) =>
                          onAnswerIntent?.(entry.batch.id!, intentId, answer)
                        }
                        onOpenConversation={onOpenConversation}
                      />
                    </div>
                  </React.Fragment>
                );
              }
              const mine = entry.from === "you";
              const prev = visible[i - 1];
              // Tuck consecutive same-sender bubbles close together and hide the
              // repeated avatar, so a multi-line agent turn reads as one block.
              const grouped = !!prev && prev.from === entry.from;
              return (
                <React.Fragment key={i}>
                  {openers[i] && (
                    <ContextDivider label={openers[i]!} conversationId={contexts[i]} />
                  )}
                  <div
                    className={cn(
                      "flex items-end gap-2",
                      mine ? "justify-end" : "justify-start",
                      grouped ? "mt-1" : "mt-2.5",
                    )}
                  >
                    {!mine && (
                      <span
                        className={cn(
                          "grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-blue/10 p-1",
                          grouped && "opacity-0",
                        )}
                      >
                        <NeuralHead />
                      </span>
                    )}
                    <div
                      className={cn(
                        "max-w-[82%] rounded-2xl border px-3.5 py-2 text-sm leading-relaxed shadow-sm",
                        mine
                          ? "rounded-br-md border-brand-blue/25 bg-brand-blue/10 text-foreground"
                          : "rounded-bl-md border-border/70 bg-card text-foreground",
                      )}
                    >
                      <AgentText
                        text={entry.text}
                        conversationId={
                          entry.from === "agent" ? (entry.conversationId ?? null) : null
                        }
                      />
                      {entry.from === "agent" && entry.conversationId && (
                        <button
                          type="button"
                          onClick={() => onOpenConversation?.(entry.conversationId!)}
                          className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-brand-blue/50 px-2 py-0.5 text-xs font-medium text-brand-blue transition hover:bg-brand-blue/10"
                        >
                          <ExternalLink className="h-2.5 w-2.5" />
                          View negotiation #{entry.conversationId.slice(0, 8)}
                        </button>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={endRef} />
          </div>
        </ScrollArea>
      </div>
      <form
        onSubmit={onSubmit}
        className="space-y-2 border-t border-border/60 bg-card px-4 py-3"
      >
          {(sigilEnabled || onConfirmModeChange) && (
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs">
              {sigilEnabled ? (
                <button
                  type="button"
                  // preventDefault on mousedown (not the action) so the textarea keeps focus,
                  // matching the dropdown rows above; the action stays on click so the button
                  // also works from the keyboard.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={insertSigilPrefix}
                  disabled={busy || locked}
                  // With the label gone (#101), these two attributes are the ONLY place the
                  // meaning survives in words. They stay, and they stay accurate.
                  title="Insert an availability pattern"
                  aria-label="Insert an availability pattern"
                  // A tinted pill, not a bare ring: pattern is blue because a pattern is time,
                  // and time is blue in this app. The glyph identifies the type; colour only
                  // reinforces, so the pill still reads with colour removed.
                  className="inline-flex items-center rounded-full border border-brand-blue/30 bg-brand-blue/10 px-2.5 py-0.5 font-medium text-brand-blue transition hover:bg-brand-blue/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="font-mono">{PATTERN_PREFIX}</span>
                </button>
              ) : (
                <span />
              )}
              {onConfirmModeChange && (
                <div className="flex items-center gap-2">
                  <span className="uppercase tracking-wide text-muted-foreground">
                    Before booking
                  </span>
                  <div className="inline-flex overflow-hidden rounded-full border border-border">
                    {(["auto", "pre_confirm"] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => onConfirmModeChange(m)}
                        aria-pressed={confirmMode === m}
                        title={
                          m === "auto"
                            ? "Book automatically; only ask when the request is ambiguous"
                            : "Always show the interpretation and wait for your go-ahead"
                        }
                        className={cn(
                          "px-2.5 py-0.5 font-medium transition",
                          confirmMode === m
                            ? "bg-brand-blue/10 text-brand-blue ring-1 ring-inset ring-brand-blue/30"
                            : "text-muted-foreground hover:bg-brand-blue/10 hover:text-brand-blue",
                        )}
                      >
                        {m === "auto" ? "Auto" : "Confirm first"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {interpretation && <MeetingContextCard interp={interpretation} />}
          {((quickReplies && quickReplies.length > 0) || clarifyKind === "date_range") && (
            <div className="space-y-1.5">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                {clarifyKind === "date_range" ? "Pick a timeframe" : "Pick one"}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {quickReplies?.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    disabled={busy}
                    onClick={() => onQuickReply?.(option)}
                    className="rounded-full border border-brand-blue/40 bg-brand-blue/5 px-3 py-1 text-xs font-medium text-brand-blue transition hover:bg-brand-blue hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {option.label}
                  </button>
                ))}
                {clarifyKind === "date_range" && (
                  <DateRangePicker
                    disabled={busy}
                    onPick={(label, value) => onQuickReply?.({ label, value })}
                  />
                )}
              </div>
            </div>
          )}
          <div className="flex items-end gap-2">
            <div className="relative flex-1">
              {sigilOpen && (
                <ul
                  role="listbox"
                  aria-label={sigilChooserOpen ? "Pick a type" : "Pick one"}
                  className="comic-shadow absolute bottom-full left-0 z-20 mb-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover py-1 text-sm"
                >
                  {sigilRows.length === 0 && (
                    <li className="px-3 py-1.5 text-muted-foreground">Nothing to pick</li>
                  )}
                  {sigilRows.map((row, i) => (
                    <li key={row.key} role="option" aria-selected={i === sigilIndex}>
                      <button
                        type="button"
                        disabled={row.disabled}
                        // mousedown (not click) so the textarea keeps focus and the
                        // active-sigil state survives the selection.
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (!row.disabled) row.onPick();
                        }}
                        onMouseEnter={() => setSigilIndex(i)}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left",
                          row.disabled
                            ? "cursor-not-allowed text-muted-foreground/60"
                            : i === sigilIndex
                              ? "bg-accent text-accent-foreground"
                              : "hover:bg-muted",
                        )}
                      >
                        <span className="min-w-0 truncate font-semibold">{row.label}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {row.disabled ? "Not available yet" : row.hint}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {mentionOpen && !sigilOpen && (
                <ul
                  role="listbox"
                  className="comic-shadow absolute bottom-full left-0 z-20 mb-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover py-1 text-sm"
                >
                  {candidates.map((c, i) => (
                    <li key={c.handle} role="option" aria-selected={i === activeIndex}>
                      <button
                        type="button"
                        // mousedown (not click) so the textarea keeps focus and
                        // the active-mention state survives the selection.
                        onMouseDown={(e) => {
                          e.preventDefault();
                          chooseCandidate(c);
                        }}
                        onMouseEnter={() => setActiveIndex(i)}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left",
                          i === activeIndex
                            ? "bg-accent text-accent-foreground"
                            : "hover:bg-muted",
                        )}
                      >
                        <span className="flex min-w-0 items-baseline gap-2">
                          <span className="font-semibold">@{c.handle}</span>
                          <span className="truncate text-muted-foreground">
                            {c.display_name}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                          {c.source === "booked"
                            ? c.meeting_count > 0
                              ? `met ${c.meeting_count}×`
                              : "met"
                            : "public"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => {
                  onDraftChange(e.target.value);
                  syncMention(e.currentTarget);
                  syncSigil(e.currentTarget);
                }}
                onKeyDown={onComposerKeyDown}
                onKeyUp={(e) => {
                  // Don't re-detect on the keys the dropdown consumes — that
                  // would reopen it right after an Enter/Escape selection.
                  if (
                    ["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(
                      e.key,
                    )
                  ) {
                    return;
                  }
                  syncMention(e.currentTarget);
                  syncSigil(e.currentTarget);
                }}
                onClick={(e) => {
                  syncMention(e.currentTarget);
                  syncSigil(e.currentTarget);
                }}
                onBlur={() => {
                  setMention(null);
                  setSigil(null);
                }}
                placeholder={
                  locked
                    ? "Sign in to ask for a meeting"
                    : mode === "outgoing"
                      ? "Find 30 minutes with @priya next week, mornings preferred."
                      : "Reply to the incoming request…"
                }
                disabled={busy || locked}
                rows={2}
                className="min-h-[60px] w-full resize-none rounded-2xl border border-border bg-background px-3.5 py-2.5 text-sm leading-relaxed shadow-sm focus-visible:border-brand-blue/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/30 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <button
              type="submit"
              disabled={busy || locked || !draft.trim()}
              className="flex h-[60px] items-center gap-2 rounded-2xl bg-brand-blue px-5 font-display text-sm font-semibold text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {busy ? "…" : "Send"}
            </button>
          </div>
          {belowComposer}
          {mode === "outgoing" && extractHandles(draft).length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Inviting
              </span>
              {extractHandles(draft).map((handle) => {
                const optional = optionalHandles?.has(handle) ?? false;
                return (
                  <button
                    key={handle}
                    type="button"
                    onClick={() => onToggleOptional?.(handle)}
                    title={
                      optional
                        ? "Optional. Click to make required"
                        : "Required. Click to make optional"
                    }
                    className={cn(
                      "rounded-full px-2 py-0.5 text-xs font-semibold transition",
                      optional
                        ? "bg-muted text-muted-foreground line-through decoration-muted-foreground/50"
                        : "bg-brand-blue/10 text-brand-blue",
                    )}
                  >
                    @{handle}
                    <span className="ml-1 no-underline opacity-70">
                      {optional ? "optional" : "required"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {footer}
        </form>
    </div>
  );
}

/**
 * A chat bubble's text, with the booked-outcome tail turned into a link to the
 * meeting on the calendar.
 *
 * <p>Splitting on {@link CALENDAR_SUFFIX} rather than parsing the sentence keeps
 * the two in lockstep: the constant is what {@code bookedOutcomeText} appends, so
 * a reworded line either matches here or falls through to plain prose. It never
 * renders a broken link.</p>
 */
function AgentText({
  text,
  conversationId,
}: {
  text: React.ReactNode;
  conversationId: string | null;
}) {
  // Bubbles may carry arbitrary nodes (error notes render markup), so only a
  // plain string is safe to split.
  const suffixAt =
    conversationId && typeof text === "string" ? text.lastIndexOf(CALENDAR_SUFFIX) : -1;
  if (suffixAt === -1 || !conversationId || typeof text !== "string") {
    return <span className="whitespace-pre-line">{text}</span>;
  }
  return (
    <span className="whitespace-pre-line">
      {text.slice(0, suffixAt)}
      <Link
        to={calendarLinkFor(conversationId)}
        className="inline-flex items-center gap-1 font-medium text-brand-blue underline underline-offset-2 hover:no-underline"
      >
        <CalendarCheck className="h-3 w-3" />
        {CALENDAR_SUFFIX}
      </Link>
    </span>
  );
}
